import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { api } from "@/common/api/instance";
import { UserRole } from "@/common/api/datasources/Api";
import * as z from "zod";

export const leaseFormSchema = z
    .object({
        propertyUUID: z.string({
            required_error: "leases.form.validation.propertyRequired",
        }),
        tenantUUID: z.string().optional().nullable(),
        managerUUID: z.string().optional().nullable(),
        startDate: z
            .date({
                required_error: "leases.form.validation.startDateRequired",
            })
            .nullable(),
        endDate: z.date().nullable(),
        monthlyRent: z.coerce
            .number({
                required_error: "leases.form.validation.rentRequired",
            })
            .min(0, "leases.form.validation.rentPositive"),
        securityDeposit: z.coerce
            .number()
            .min(0, "leases.form.validation.depositPositive")
            .optional(),
        paymentDay: z.coerce
            .number({
                required_error: "leases.form.validation.paymentDayRequired",
            })
            .min(1, "leases.form.validation.paymentDayRange")
            .max(31, "leases.form.validation.paymentDayRange"),
        status: z
            .enum(["ACTIVE", "PENDING", "COMPLETED", "CANCELED"])
            .default("PENDING"),
        autoRenew: z.boolean().default(false),
        notes: z.string().optional(),
    })
    .refine(
        (data) => {
            if (data.endDate && data.startDate) {
                return data.endDate > data.startDate;
            }
            return true;
        },
        {
            message: "leases.form.validation.endDateAfterStart",
            path: ["endDate"],
        },
    );

export type LeaseFormValues = z.infer<typeof leaseFormSchema>;

interface UseLeaseFormProps {
    initialData?: Partial<LeaseFormValues>;
    readOnly?: boolean;
}

export const useLeaseForm = ({ initialData, readOnly }: UseLeaseFormProps = {}) => {
    const form = useForm<LeaseFormValues>({
        resolver: zodResolver(leaseFormSchema),
        defaultValues: {
            propertyUUID: initialData?.propertyUUID ?? "",
            tenantUUID: initialData?.tenantUUID ?? null,
            managerUUID: initialData?.managerUUID ?? null,
            startDate: initialData?.startDate ? new Date(initialData.startDate) : null,
            endDate: initialData?.endDate ? new Date(initialData.endDate) : null,
            monthlyRent: initialData?.monthlyRent ?? 0,
            securityDeposit: initialData?.securityDeposit ?? 0,
            paymentDay: initialData?.paymentDay ?? 1,
            status: initialData?.status ?? "PENDING",
            autoRenew: initialData?.autoRenew ?? false,
            notes: initialData?.notes ?? "",
        },
    });

    useEffect(() => {
        if (initialData) {
            Object.entries(initialData).forEach(([key, value]) => {
                if (value !== undefined) {
                    const fieldKey = key as keyof LeaseFormValues;
                    if (
                        (key === "startDate" || key === "endDate") &&
                        (typeof value === "string" || value instanceof Date)
                    ) {
                        form.setValue(fieldKey, new Date(value));
                    } else {
                        form.setValue(fieldKey, value);
                    }
                }
            });

            if (initialData.managerUUID) {
                form.setValue("managerUUID", initialData.managerUUID);
            }

            if (initialData.tenantUUID) {
                form.setValue("tenantUUID", initialData.tenantUUID);
            }
        }
    }, [initialData]);

    const fetchProperties = async (context: {
        pageParam?: number;
        queryKey: string[];
    }) => {
        const pageParam = context.pageParam || 0;
        const search = context.queryKey[1] || "";

        const response = await api.properties.propertiesControllerFindAll({
            page: pageParam,
            size: 10,
            search,
        });

        return {
            content:
                response.data.content?.map((property) => ({
                    uuid: property.propertyUUID,
                    name: property.name,
                    address: property.address,
                    city: property.city,
                    type: property.type,
                    monthlyPrice: property.price,
                    rooms: property.rooms,
                    totalArea: property.area,
                    value: property.propertyUUID,
                    label: property.name,
                })) || [],
            page: response.data.page || 0,
            size: response.data.size || 10,
            numberOfElements: response.data.numberOfElements || 0,
            totalElements: response.data.totalElements || 0,
            totalPages: response.data.totalPages || 0,
            last: response.data.last || false,
            first: response.data.page === 0,
        };
    };

    const fetchTenants = async (context: {
        pageParam?: number;
        queryKey: string[];
    }) => {
        const pageParam = context.pageParam || 0;
        const search = context.queryKey[1] || "";

        const response = await api.users.usersControllerFindAll({
            page: pageParam,
            size: 10,
            role: UserRole.TENANT,
            keyword: search,
        });

        return {
            content:
                response.data.content?.map((tenant) => ({
                    uuid: tenant.userUUID,
                    firstName: tenant.firstName || "",
                    lastName: tenant.lastName || "",
                    email: tenant.email,
                    value: tenant.userUUID,
                    label: `${tenant.firstName || ""} ${tenant.lastName || ""}`,
                })) || [],
            page: response.data.page || 0,
            size: response.data.size || 10,
            numberOfElements: response.data.numberOfElements || 0,
            totalElements: response.data.totalElements || 0,
            totalPages: response.data.totalPages || 0,
            last: response.data.last || false,
            first: response.data.page === 0,
        };
    };

    const fetchManagers = async (context: {
        pageParam?: number;
        queryKey: string[];
    }) => {
        const pageParam = context.pageParam || 0;
        const search = context.queryKey[1] || "";

        const response = await api.users.usersControllerFindAll({
            page: pageParam,
            size: 10,
            role: UserRole.STAFF,
            keyword: search,
        });

        const staffCount = response.data.totalElements || 0;
        let content = response.data.content || [];

        if (staffCount === 0) {
            const adminResponse = await api.users.usersControllerFindAll({
                page: 0,
                size: 10,
                role: UserRole.ADMIN,
                keyword: search,
            });
            content = adminResponse.data.content || [];
        }

        return {
            content:
                content.map((manager) => ({
                    uuid: manager.userUUID,
                    firstName: manager.firstName || "",
                    lastName: manager.lastName || "",
                    email: manager.email,
                    value: manager.userUUID,
                    label: `${manager.firstName || ""} ${manager.lastName || ""}`,
                })) || [],
            page: response.data.page || 0,
            size: response.data.size || 10,
            numberOfElements: response.data.numberOfElements || 0,
            totalElements: response.data.totalElements || 0,
            totalPages: response.data.totalPages || 0,
            last: response.data.last || false,
            first: response.data.page === 0,
        };
    };

    return {
        form,
        fetchProperties,
        fetchTenants,
        fetchManagers,
        isEditMode: !!initialData?.propertyUUID,
    };
}; 